import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";
import { showSuccess, showError } from "toast/toast";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { getAuthCookie } from "../../utils/authCookies";

type State = {
  _id: string;
  countryId: string;
  name: string;
  code: string;
  created_by?: {
    id: string;
    name: string;
  };
  updated_by?: {
    id: string;
    name: string;
  };
  created_at?: string;
  updated_at?: string;
};

type Country = {
  _id: string;
  name: string;
  code: string;
};

const columnHelper = createColumnHelper<State>();

const SuperAdminStates = () => {
  const { countryId } = useParams<{ countryId: string }>();
  const [states, setStates] = useState<State[]>([]);
  const [data, setData] = useState<State[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");

  const location = useLocation();
  const navigate = useNavigate();

  // Fetch countries for the dropdown
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/location/countries`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setCountries(data || []);
      } catch (err) {
        console.error("Error fetching countries:", err);
      }
    };
    fetchCountries();
  }, [token]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/location/states/country/${countryId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        console.log("Fetched States:", result);
        setStates(result || []);
        setData(result || []);
      } catch (err) {
        setStates([]);
        setData([]);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };    

    fetchStates();
  }, [token, countryId, deleting]);

  // Formik for Add/Edit Modal
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      countryId: selectedState?.countryId || countryId || '',
      name: selectedState?.name || '',
      code: selectedState?.code || '',
    },
    validationSchema: Yup.object({
      countryId: Yup.string().required('Country is required'),
      name: Yup.string()
        .min(2, 'State name must be at least 2 characters long')
        .max(50, 'State name cannot be longer than 50 characters')
        .required('State name is required'),
      code: Yup.string()
        .min(2, 'State code must be at least 2 characters long')
        .required('State code is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      setSubmitting(true);
      try {
        const url = modalMode === 'add' 
          ? `${process.env.REACT_APP_API_URL}/location/states`
          : `${process.env.REACT_APP_API_URL}/location/states/${selectedState?._id}`;
        
        const method = modalMode === 'add' ? 'POST' : 'PUT';
        
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            countryId: values.countryId,
            name: values.name,
            code: values.code,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to save state');
        }

        showSuccess(modalMode === 'add' ? 'State added successfully!' : 'State updated successfully!');
        
        setShowModal(false);
        resetForm();
        setSelectedState(null);
        
        // Refresh the data
        const refreshResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/location/states/country/${countryId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const refreshData = await refreshResponse.json();
        setStates(refreshData || []);
        setData(refreshData || []);
        
      } catch (error: any) {
        showError(error.message || 'An error occurred while saving the state.');
        console.error('Error submitting form:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleAddClick = () => {
    setModalMode('add');
    setSelectedState(null);
    formik.resetForm();
    setShowModal(true);
  };

  const handleEditClick = (state: State) => {
    setModalMode('edit');
    setSelectedState(state);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedState(null);
    formik.resetForm();
  };

  const handleDelete = async (stateId: string) => {
    const confirmation = window.confirm("Are you sure you want to delete this state?");
    if (!confirmation) return;

    setDeleting(stateId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/location/states/${stateId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the state.");
      }

      showSuccess("State deleted successfully!");
      setStates((prev) =>
        prev.filter((state) => state._id !== stateId)
      );
      setData((prev) =>
        prev.filter((state) => state._id !== stateId)
      );
      setDeleting(null);
    } catch (err) {
      showError("Failed to delete the state.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  // Define table columns using TanStack Table
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text">S.No.</div>,
        cell: (info) => (
          <div className="fw-bold fs-16" style={{ color: '#2563eb' }}>
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="th-text">State Name</div>,
        cell: (info) => (
          <Link 
            to={`/superadmin/cities/${info.row.original._id}`}
            className="fw-normal fs-16 text-decoration-none"
            style={{ color: '#374151' }}
          >
            {info.getValue()}
          </Link>
        ),
      }),

      columnHelper.accessor("code", {
        id: "code",
        header: () => <div className="th-text">Code</div>,
        cell: (info) => (
          <div className="fw-normal fs-16" style={{ color: '#6b7280' }}>
            {info.getValue()}
          </div>
        ),
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const state = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <Link
                to={`/superadmin/cities/${state._id}`}
                className="btn btn-info btn-sm fw-bold fs-14"
                title="View Counties"
              >
                <i className="ri-map-pin-line me-1" />
                Counties
              </Link>
              <button
                type="button"
                className="btn btn-warning icon-square p-0"
                style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Edit"
                onClick={() => handleEditClick(state)}
              >
                <i className="ri-pencil-fill fs-5" />
              </button>
              <DeleteIconButton
                title="Delete"
                className="icon-square danger"
                onClick={() => handleDelete(state._id)}
                disabled={deleting === state._id}
              />
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, countryId]);

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(data.length / pagination.pageSize),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
  });

  return (
    <div className="content">
      <style>
        {`
          .btn-warning.icon-square:hover i {
            color: #856404 !important;
          }
        `}
      </style>
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0">States</h4>
        <div className="d-flex gap-3 align-items-center">
          <button
            type="button"
            onClick={handleAddClick}
            className="btn btn-primary table-addbutton"
          >
            <i className="ri-add-line me-1" />
            Add State
          </button>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header */}
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">States List</h5>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="serial"
            titleColId="name"
            actionColId="actions"
          />
        </div>
      </div>
      
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          {loading ? "Loading states..." : error ? `Error: ${error}` : "No states found"}
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="fw-bold">
            {modalMode === 'add' ? 'Add State' : 'Edit State'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="row g-3">
              <div className="col-md-12">
                <label className="form-label fw-semibold">
                  Country <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-control ${formik.touched.countryId && formik.errors.countryId ? 'is-invalid' : ''}`}
                  id="countryId"
                  name="countryId"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.countryId}
                >
                  <option value="">- Select Country -</option>
                  {countries && countries.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {formik.touched.countryId && formik.errors.countryId ? (
                  <div className="invalid-feedback d-block">{formik.errors.countryId}</div>
                ) : null}
              </div>
              <div className="col-md-12">
                <label className="form-label fw-semibold">
                  State Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''}`}
                  id="name"
                  name="name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder="Enter state name"
                />
                {formik.touched.name && formik.errors.name ? (
                  <div className="invalid-feedback">{formik.errors.name}</div>
                ) : null}
              </div>
              <div className="col-md-12">
                <label className="form-label fw-semibold">
                  State Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formik.touched.code && formik.errors.code ? 'is-invalid' : ''}`}
                  id="code"
                  name="code"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.code}
                  placeholder="Enter state code (e.g., NY)"
                />
                {formik.touched.code && formik.errors.code ? (
                  <div className="invalid-feedback">{formik.errors.code}</div>
                ) : null}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="border-top pt-3">
          <button
            type="button"
            className="btn back-btn surface-btn fw-bold fs-16"
            onClick={handleCloseModal}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary save-btn surface-btn fw-bold fs-16"
            onClick={() => formik.handleSubmit()}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              modalMode === 'add' ? 'Add State' : 'Update State'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuperAdminStates;
